/**
 * 密码条目查看模态框
 * 显示完整密码信息，支持复制、自动填充调用
 */

import React, { useState } from "react";
import type { DecryptedItem } from "@passkeeper/shared";
import { CATEGORY_NAMES } from "@passkeeper/shared";

interface Props {
  item: DecryptedItem;
  showToast: (msg: string, type: "success" | "error" | "info") => void;
  onClose: () => void;
  onEdit: () => void;
}

export default function ItemModal({ item, showToast, onClose, onEdit }: Props) {
  const [showPassword, setShowPassword] = useState(false);
  const [showNotes, setShowNotes] = useState(false);

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast(`${label} 已复制到剪贴板`, "success");
    } catch {
      showToast("复制失败", "error");
    }
  };

  const handleSuperpower = () => {
    // "调用超级密码"：尝试自动填充到当前浏览器页面
    // 如果在浏览器插件上下文，会尝试注入
    try {
      // 尝试 postMessage 给 extension 层
      window.postMessage(
        {
          type: "PASSKEEPER_AUTOFILL",
          payload: {
            url: item.url,
            username: item.username,
            password: item.password,
          },
        },
        "*"
      );
      showToast("已发送自动填充指令", "info");
    } catch {
      showToast("自动填充需要浏览器插件支持", "info");
    }
  };

  const categoryClass = `category-badge category-${item.category}`;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {item.favicon && (
              <img src={item.favicon} alt="" width="24" height="24" />
            )}
            <h2>{item.title}</h2>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="modal-body">
          {/* 站点信息 */}
          <div style={{ marginBottom: 16 }}>
            <span className={categoryClass} style={{ marginRight: 8 }}>
              {CATEGORY_NAMES[item.category]}
            </span>
            <a href={item.url} target="_blank" rel="noreferrer" style={{ fontSize: "0.85rem" }}>
              {item.domain || item.url}
            </a>
          </div>

          {/* 账号 */}
          <div className="form-group">
            <label>账号</label>
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                padding: "10px 12px",
                background: "#f8fafc",
                borderRadius: 8,
                border: "1px solid #e2e8f0",
              }}
            >
              <span style={{ flex: 1 }}>{item.username}</span>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => copyToClipboard(item.username, "账号")}
              >
                📋
              </button>
            </div>
          </div>

          {/* 密码 */}
          <div className="form-group">
            <label>密码</label>
            <div className="password-field">
              <input
                type={showPassword ? "text" : "password"}
                value={item.password}
                readOnly
              />
              <button
                className="toggle-visibility"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? "🙈" : "👁"}
              </button>
            </div>
          </div>

          {/* 密码操作 */}
          <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => copyToClipboard(item.password, "密码")}
            >
              📋 复制密码
            </button>
            <button className="btn btn-primary btn-sm" onClick={handleSuperpower}>
              🚀 调用（自动填充）
            </button>
          </div>

          {/* 备注 */}
          {item.notes && (
            <div className="form-group">
              <label>
                备注
                <button
                  className="btn btn-ghost btn-sm"
                  style={{ marginLeft: 8, padding: "2px 6px", fontSize: "0.75rem" }}
                  onClick={() => setShowNotes(!showNotes)}
                >
                  {showNotes ? "隐藏" : "显示"}
                </button>
              </label>
              {showNotes && (
                <textarea
                  value={item.notes}
                  readOnly
                  rows={3}
                  style={{ resize: "none" }}
                />
              )}
            </div>
          )}

          {/* 元信息 */}
          <div style={{ fontSize: "0.75rem", color: "#94a3b8", marginTop: 16 }}>
            创建于 {new Date(item.createdAt).toLocaleString()}
            {item.updatedAt > item.createdAt &&
              ` · 更新于 ${new Date(item.updatedAt).toLocaleString()}`}
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>
            关闭
          </button>
          <button className="btn btn-primary" onClick={onEdit}>
            编辑
          </button>
        </div>
      </div>
    </div>
  );
}